// ข้อมูลบนการ์ดรูปภาพสำหรับแชร์ผลชีวิต (docs/PLAN.md เฟส 4)
// ส่วนนี้จัดข้อความล้วนๆ ทดสอบได้โดยไม่ต้องมีเบราว์เซอร์ ส่วนวาดลง canvas อยู่ที่ src/client/share-card.ts

import { stagesOf, summarize } from "../engine/life";
import { DEATH_CAUSE_TH, displayName, formatAge } from "../engine/text";
import type { LifeState } from "../engine/types";

export interface ShareStat {
  label: string;
  value: string;
  sub?: string; // บรรทัดเล็กใต้ค่า เช่น ชื่อนักล่า
}

export interface ShareCardData {
  name: string;
  sci: string;
  generation: number | null; // รุ่นที่เท่าไรของสายตระกูล (null = รุ่นแรก)
  stats: ShareStat[];
  places: string | null; // เกิดที่ไหน จากไปที่ไหน เดินทางไกลเท่าไร
  achievements: string[]; // ความสำเร็จที่ได้จากชีวิตนี้
}

export interface SharePlaces {
  born: string | null;
  died: string | null;
}

const num = (n: number) => n.toLocaleString("th-TH");

export function shareCardData(life: LifeState, places: SharePlaces, achievements: readonly string[] = []): ShareCardData {
  const s = summarize(life);
  const stage = stagesOf(life.species).find((x) => x.id === s.stageId);
  const worker = life.flags.includes("role_worker");
  const km = Math.round(life.geo?.km ?? 0);
  const where = [
    places.born && `เกิดที่${places.born}`,
    places.died && places.died !== places.born ? `จากไปที่${places.died}` : null,
    km >= 1 ? `เดินทางรวม ${num(km)} กม.` : null,
  ].filter(Boolean);
  return {
    name: displayName(life.species),
    sci: life.species.sci,
    generation: life.lineage && life.lineage.generation > 1 ? life.lineage.generation : null,
    stats: [
      { label: "อายุ", value: formatAge(s.ageDays) },
      { label: worker ? "น้องที่ช่วยเลี้ยง" : "ลูกหลาน", value: num(s.offspring) },
      { label: "ช่วงวัยสุดท้าย", value: stage?.nameTh ?? "-" },
      {
        label: "สาเหตุ",
        value: s.cause ? DEATH_CAUSE_TH[s.cause] : "-",
        ...(life.killer ? { sub: life.killer.nameTh ?? life.killer.sci } : {}),
      },
    ],
    places: where.length ? where.join(" · ") : null,
    achievements: achievements.slice(0, 3),
  };
}

/**
 * ตัดข้อความเป็นบรรทัดให้กว้างไม่เกิน maxWidth ภาษาไทยไม่เว้นวรรคระหว่างคำ จึงตัดตามคำ (Intl.Segmenter)
 * ถ้าไม่มี Segmenter ตัดทีละตัวอักษร คำที่ยาวเกินบรรทัดเดียวก็ตัดกลางคำ
 */
export function wrapText(text: string, maxWidth: number, measure: (s: string) => number, maxLines = Infinity): string[] {
  const words = segments(text);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    // เครื่องหมายวรรคตอนติดไปกับคำก่อนหน้าเสมอ ไม่ขึ้นบรรทัดใหม่เดี่ยวๆ (เช่น "กม.")
    if (measure(line + w) <= maxWidth || !line || /^[.,:;!?…)\]]+$/.test(w)) {
      line += w;
      continue;
    }
    lines.push(line.trimEnd());
    line = w.trimStart();
  }
  if (line) lines.push(line.trimEnd());
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  let last = kept[maxLines - 1];
  while (last && measure(`${last}…`) > maxWidth) last = last.slice(0, -1);
  kept[maxLines - 1] = `${last}…`;
  return kept;
}

function segments(text: string): string[] {
  const Seg = (Intl as { Segmenter?: new (l: string, o: { granularity: string }) => { segment: (s: string) => Iterable<{ segment: string }> } }).Segmenter;
  if (Seg) return [...new Seg("th", { granularity: "word" }).segment(text)].map((x) => x.segment);
  return [...text];
}
